import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTrainingPlans } from '../api/athlete';
import { useAuth } from '../contexts/AuthContext';
import { TrainingPlan } from '../types/athlete';
import { Plus, Calendar, ArrowRight, Loader } from 'lucide-react';
import { formatRaceDateWithCountdown, calculateDaysUntilRace, getProximityIcon } from '../lib/utils/dateUtils';

const TrainingPlanList: React.FC = () => {
  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    const loadPlans = async () => {
      try {
        const data = await getTrainingPlans(user.id);

        // Sort plans by nearest race first (active/draft plans with upcoming races)
        const sortedPlans = data.sort((a, b) => {
          const daysA = calculateDaysUntilRace(a.end_date);
          const daysB = calculateDaysUntilRace(b.end_date);

          // Active/draft plans with future races come first
          const aIsUpcoming = (a.status === 'active' || a.status === 'draft') && daysA >= 0;
          const bIsUpcoming = (b.status === 'active' || b.status === 'draft') && daysB >= 0;

          if (aIsUpcoming && !bIsUpcoming) return -1;
          if (!aIsUpcoming && bIsUpcoming) return 1;
          if (aIsUpcoming && bIsUpcoming) return daysA - daysB;

          // For past/completed plans, sort by end date descending
          return new Date(b.end_date).getTime() - new Date(a.end_date).getTime();
        });

        if (isMounted) setPlans(sortedPlans);
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadPlans();
    return () => { isMounted = false; };
  }, [user]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Your Training Plans</h1>
          <p className="text-muted">Manage and track your triathlon training programs</p>
        </div>
        <Link
          to="/plans/new"
          className="flex items-center space-x-2 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors no-underline"
        >
          <Plus size={18} />
          <span>Create New Plan</span>
        </Link>
      </div>

      {plans.length === 0 ? (
        <div className="card-base p-8 text-center">
          <Calendar className="mx-auto text-[#3b82f6] mb-4" size={48} />
          <h2 className="text-xl font-semibold mb-2">No Training Plans Yet</h2>
          <p className="text-muted mb-4">Create your first training plan to start tracking your progress.</p>
          <Link
            to="/plans/new"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors no-underline"
          >
            <Plus size={18} />
            <span>Create Your First Plan</span>
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {plans.map(plan => {
            const raceInfo = formatRaceDateWithCountdown(plan.end_date);
            const icon = getProximityIcon(raceInfo.proximityStatus);

            return (
              <Link
                key={plan.id}
                to={`/plans/${plan.id}`}
                className="card-base p-6 flex items-center justify-between no-underline group"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h2 className="text-xl font-semibold group-hover:text-[#3b82f6] transition-colors">
                      {plan.name}
                    </h2>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      plan.status === 'active' ? 'bg-green-500/10 text-green-400' :
                      plan.status === 'completed' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-gray-500/10 text-gray-400'
                    }`}>
                      {plan.status}
                    </span>
                  </div>
                  <p className="text-muted mt-1 mb-3">{plan.description || 'No description'}</p>

                  <div className="flex items-center space-x-4 text-sm">
                    <span className="flex items-center space-x-1 text-[#718096]">
                      <Calendar size={14} />
                      <span>{plan.start_date}</span>
                      <span>→</span>
                      <span>{plan.end_date}</span>
                    </span>
                    <span className="flex items-center space-x-2">
                      <span className="text-lg">{icon}</span>
                      <span className={`font-medium ${raceInfo.colorClass.split(' ')[0]}`}>
                        {raceInfo.daysUntil >= 0 ? raceInfo.displayText : 'Race completed'}
                      </span>
                    </span>
                  </div>
                </div>
                <ArrowRight className="text-[#3b82f6] group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrainingPlanList;
